import React, { useState, useMemo, useContext, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  ButtonGroup,
  CardFooter
} from "reactstrap";
import { Line } from "react-chartjs-2";
import placeholder from "assets/img/placeholder.jpg";
import { DataContext } from "../context/DataContext";
import { createProduct, updateProduct } from "../context/api";
import { s3ImageUrl } from "../utils/s3Url";
import { API_BASE } from "../config/api";

const normalizeUnitOfMeasure = value => String(value || "").trim().toLowerCase();
const isUnitProduct = value => {
  const normalized = normalizeUnitOfMeasure(value);
  return normalized === "unit" || normalized === "units";
};
const normalizeUnitsPerCaseInput = value => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(Math.trunc(parsed)) : "";
};

// Dummy data for products



const Products = () => {
  const { data, setData } = useContext(DataContext);
  const unitOfMeasure = data.unitsOfMeasure || [];
  const initialProducts = data.products || [];
  // console.log("Products data from context:", initialProducts);
  const isSuperuser = data.userProfile?.is_superuser === true;
  // Main state for products
  // With an inline dedupe-by-id, then sort A→Z by name
  const brands = data.brands
    ? Array.from(
        new Map(
          data.brands
            .filter(b => !b.is_archived)       // exclude archived if you like
            .map(b => [b.id, b])
        ).values()
      ).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    : [];
  // console.log("Brands from context:", brands);
  const categories = data.categories || []; // Added categories

  // Helper: map any brand candidate to the canonical brand from context (by id or name)
  const getCanonicalBrand = (candidate) => {
    if (!candidate) return null;
    const byId = candidate.id != null
      ? brands.find(b => String(b.id) === String(candidate.id))
      : null;
    if (byId) return { ...byId };
    if (candidate.name) {
      const byName = brands.find(b => b.name === candidate.name);
      if (byName) return { ...byName };
      // fallback to keep name we have if not found in context
      return { id: candidate.id ?? candidate.name, name: candidate.name };
    }
    return null;
  };

  // Filter and sort states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [showPromotion, setShowPromotion] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [hover, setHover] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imageSrcForDetailModal, setImageSrcForDetailModal] = useState(placeholder);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState(null);

  // Disable double submit while saving
  const [isSavingProduct, setIsSavingProduct] = useState(false);


  // Modal and selected product state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [isAddingBrand, setIsAddingBrand] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [archiveConfirmModalOpen, setArchiveConfirmModalOpen] = useState(false);
  const searchMatches = useMemo(() => {
    if (!searchText.trim()) return [];
    return initialProducts
      .filter(p => p.name.toLowerCase().includes(searchText.toLowerCase()))
      .slice(0, 10); // limit results
  }, [searchText, initialProducts]);

  const promotedProductIds = useMemo(() => {
    const ids = new Set();
    if (!data.promotions || !data.products) {
      return ids;
    }

    // - not archived, and
    // - startDate is null/empty OR startDate is today or in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isActiveByStartDate = (p) => {
      if (!p.startDate) return true; // show if null
      // Accept formats like "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss"
      const iso = typeof p.startDate === 'string' ? p.startDate.slice(0, 10) : p.startDate;
      const d = new Date(iso);
      if (isNaN(d)) return true; // if unparsable, do not hide
      d.setHours(0, 0, 0, 0);
      return d <= today;
    };

    const activePromotions = (data.promotions || []).filter(p => !p.archived && isActiveByStartDate(p));
    
    for (const promo of activePromotions) {
      const productIds = promo.products;
      // console.log("Active promotions:", activePromotions, data.promotions);
      // Case 1: targets specific products
      if (Array.isArray(productIds) && productIds.length > 0) {
        productIds.forEach(id => ids.add(id));
      }
      // Case 2: targets all customers for a brand
      else if (promo.allCustomers === true && promo.brandId) {
        data.products.forEach(product => {
          if (product.brand && product.brand.id == promo.brandId) {
            ids.add(product.id);
          }
        });
      }
    }

    return ids;
  }, [data.promotions, data.products]);
  
  // Form state for editing/adding a product
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    code: "",
    unitOfMeasure: "",
    unitsPerCase: "",
    productType: "",
    brand: { isNew: false, id: "", name: "" },
    defaultPrices: { tier1: "", tier2: "", tier3: "" },
    image: "", // This will store the S3 URL or local blob URL
    salesData: { labels: [], data: [] },
    topCustomers: [],
    archived: false,
  });

  const [newBrand, setNewBrand] = useState({
    name: "",
    category: "",
    owner: "",
    description: "",
  });


  const [errors, setErrors] = useState({});


  useEffect(() => {
    if (detailModalOpen && formData) {
      let newSrc = placeholder;
      if (
        formData.image &&
        (formData.image.startsWith('blob:') ||
         formData.image.startsWith('data:'))
      ) {
        newSrc = formData.image; // Use local blob if available (previewing new upload)
      } else if (formData.image && formData.image.startsWith('http')) {
        // If formData.image is already an S3 URL (potentially with a timestamp),
        // use it directly. openDetailModal should have added a fresh timestamp if needed.
        newSrc = formData.image;
      } else if (formData.id) {
        // Fallback: existing product, construct S3 URL with a fresh timestamp
        newSrc = `${s3ImageUrl("products", formData.id, ".png")}?timestamp=${new Date().getTime()}`;
      } else if (formData.image) {
        // Fallback for non-blob, non-http, non-ID image string (e.g. initial placeholder string)
        newSrc = formData.image;
      }
      setImageSrcForDetailModal(newSrc);
    } else if (!detailModalOpen) {
      setImageSrcForDetailModal(placeholder);
    }
  }, [formData, detailModalOpen]);


  const filteredProducts = useMemo(() => {
    let raw = initialProducts.filter(p => {
      if (searchText && !p.name.toLowerCase().includes(searchText.toLowerCase())) {
        return false;
      }
      if (selectedBrand && (!p.brand || p.brand.name !== selectedBrand)) {
        return false;
      }
      if (selectedType && p.productType !== selectedType) {
        return false;
      }
      if (showPromotion && !promotedProductIds.has(p.id)) return false;
      if (!showArchived && p.archived) return false;
      return true;
    });
    raw = Array.from(new Map(raw.map(p => [p.id, p])).values());
    raw.sort((a, b) => {
      if (sortBy === 'name') {
        return sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      } else if (sortBy === 'price_asc') {
        const priceA = parseFloat(a.defaultPrices?.tier1);
        const priceB = parseFloat(b.defaultPrices?.tier1);
        if (isNaN(priceA) && isNaN(priceB)) return 0;
        if (isNaN(priceA)) return 1; // Put NaNs at the end for ascending
        if (isNaN(priceB)) return -1;
        return priceA - priceB;
      } else if (sortBy === 'price_desc') {
        const priceA = parseFloat(a.defaultPrices?.tier1);
        const priceB = parseFloat(b.defaultPrices?.tier1);
        if (isNaN(priceA) && isNaN(priceB)) return 0;
        if (isNaN(priceA)) return 1; // Put NaNs at the end for descending
        if (isNaN(priceB)) return -1;
        return priceB - priceA;
      }
      // Add other sort cases if needed
      return 0;
    });
    return raw;
  }, [initialProducts, searchText, selectedBrand, selectedType, showPromotion, showArchived, sortBy, sortOrder, promotedProductIds]);
  
  
  
  // Handlers for opening modals
  const openDetailModal = prod => {
    setSelectedProduct(prod);
    let imageUrlToSet = placeholder;
    if (prod.image && prod.image.startsWith('http')) {
      // If it's an S3 URL, add/update timestamp to break cache when modal opens
      imageUrlToSet = `${prod.image.split('?')[0]}?timestamp=${new Date().getTime()}`;
    } else if (prod.image) {
      imageUrlToSet = prod.image; // Could be a placeholder string or an old local path
    } else if (prod.id) {
      // If no image URL but has ID, generate S3 URL with timestamp
      imageUrlToSet = `${s3ImageUrl("products", prod.id, ".png")}?timestamp=${new Date().getTime()}`;
    }

    setFormData({
      ...prod,
      unitsPerCase: normalizeUnitsPerCaseInput(prod.unitsPerCase),
      image: imageUrlToSet,
      promotion: promotedProductIds.has(prod.id),
      brand: { ...prod.brand, isNew: false }
    });
    setIsEditing(false);
    setErrors({});
    setSelectedImageFile(null);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setErrors({});
    setSelectedImageFile(null);
  };

  const openAddModal = () => {
    setFormData({
      id: null,
      name: "",
      code: "",
      unitOfMeasure: "",
      unitsPerCase: "",
      productType: "",
      brand: { isNew: true, id: "", name: "" },
      defaultPrices: { tier1: "", tier2: "", tier3: "" },
      image: "", // Start with empty or placeholder for new product
      salesData: { labels: [], data: [] },
      topCustomers: [],
      promotion: false,
      archived: false,
    });
    setNewBrand({ name: "", category: "", owner: "", description: "" });
    setIsEditing(false);
    setErrors({});
    setSelectedImageFile(null);
    setAddModalOpen(true);
  };
  const closeAddModal = () => {
    setAddModalOpen(false);
    setErrors({});
    setSelectedImageFile(null);
  };

  // Archive confirmation modal handlers
  const openArchiveConfirmModal = () => setArchiveConfirmModalOpen(true);
  const closeArchiveConfirmModal = () => setArchiveConfirmModalOpen(false);

  const handleConfirmArchive = async () => {
    if (!formData || !formData.id) return;

    const newArchivedStatus = !formData.archived; // Toggle current status
    const updatedProductData = { ...formData, archived: newArchivedStatus };

    // Optimistic update in UI
    const originalProducts = data.products; // Store original products for revert
    setData(d => ({
      ...d,
      products: d.products.map(p =>
        p.id === formData.id ? updatedProductData : p
      ),
    }));

    try {
      await updateProduct(formData.id, updatedProductData); // API call
    } catch (error) {
      console.error(`Failed to ${newArchivedStatus ? "archive" : "unarchive"} product:`, error);
      // Revert optimistic update if API call fails
      setData(d => ({
        ...d,
        products: originalProducts, // Revert to original products list
      }));
      // Optionally, display an error message to the user here
    }

    closeArchiveConfirmModal();
    closeDetailModal();
  };


  // form handlers
  const handleFormChange = e => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, key] = name.split(".");
      setFormData(f => ({
        ...f,
        [parent]: { ...f[parent], [key]: value }
      }));
    } else {
      setFormData(f => ({ ...f, [name]: value }));
    }
  };



  // Convert JPEG to PNG via canvas
  function convertToPng(file) {
    return new Promise((resolve, reject) => {
      if (!file || !file.type) {
        return reject(new Error("Invalid file object."));
      }
      const isSupportedType = /^image\/(jpeg|jpg|png)$/i.test(file.type); // Corrected regex
      if (!isSupportedType) {
        return reject(new Error("File type not supported. Please upload JPG or PNG."));
      }
      if (/^image\/png$/i.test(file.type)) { // If already PNG // Corrected regex
        return resolve(file);
      }

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                return reject(new Error("Canvas to Blob conversion failed"));
              }
              const pngFile = new File(
                [blob],
                file.name.replace(/\\.[^/.]+$/, '') + '.png',
                { type: 'image/png' }
              );
              resolve(pngFile);
            },
            'image/png'
          );
        };
        img.onerror = (err) => reject(new Error(`Image load error: ${err.message || err}`));
        img.src = reader.result;
      };
      reader.onerror = (err) => reject(new Error(`File reader error: ${err.message || err}`));
      reader.readAsDataURL(file);
    });
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const pngFile = await convertToPng(file);
      setSelectedImageFile(pngFile); // Store the processed file for S3 upload

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: reader.result // Local blob/data URL for preview
        }));
      };
      reader.readAsDataURL(pngFile);
    } catch (err) {
      console.error('Image processing failed:', err);
      alert(`Image processing failed: ${err.message}`);
      setSelectedImageFile(null);
      
      let fallbackImageUrl = placeholder;
      // When an image processing fails, revert the preview (formData.image)
      // to a freshly timestamped version of what it was when the modal opened.
      if (isEditing && selectedProduct) { // selectedProduct is the state of the product when modal was opened.
        const originalImageAtModalOpen = selectedProduct.image;
        if (originalImageAtModalOpen && originalImageAtModalOpen.startsWith('http')) {
          // If original image was an S3 URL, re-timestamp it.
          fallbackImageUrl = `${originalImageAtModalOpen.split('?')[0]}?timestamp=${new Date().getTime()}`;
        } else if (originalImageAtModalOpen) {
          // If it was some other string (e.g. placeholder, old local path, empty string), use it.
          fallbackImageUrl = originalImageAtModalOpen;
        } else if (selectedProduct.id) {
          // If no original image string, but we have an ID, generate a fresh S3 URL for fallback.
          fallbackImageUrl = `${s3ImageUrl("products", selectedProduct.id, ".png")}?timestamp=${new Date().getTime()}`;
        }
        // This setFormData will trigger the useEffect for imageSrcForDetailModal.
        // The useEffect will then use this (potentially re-timestamped) fallbackImageUrl.
        setFormData(prev => ({ ...prev, image: fallbackImageUrl }));
      }
    }
  };
  
  // Upload to S3
  async function uploadToS3Internal(file, productId) {
    const API = API_BASE;    
    const presignedUrlPayload = {
      fileType: file.type,
      folder: 'products', // Changed folder to 'products'
      userId: String(productId) 
    };

    const res = await fetch(`${API}/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(presignedUrlPayload),
    });

    if (!res.ok) {
      let backendMessage = '';
      try {
        const errorResponse = await res.json();
        backendMessage = errorResponse.message || '';
      } catch (e) {
        try {
            const textError = await res.text();
            if (textError) backendMessage = textError;
        } catch (textE) {
            // Fallback, no specific backend message obtained
        }
      }
      // Construct a cleaner message for the alert
      const statusText = res.statusText ? ` ${res.statusText}` : ''; // Add statusText if available
      const finalErrorMessage = backendMessage ? backendMessage : `HTTP error ${res.status}${statusText}`;
      throw new Error(`Failed to get upload URL: ${finalErrorMessage}`);
    }
    
    const { uploadUrl, key: returnedKey } = await res.json();

    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!uploadRes.ok) {
      const errorText = await uploadRes.text();
      console.error('S3 PUT status:', uploadRes.status, errorText);
      // Construct a cleaner message for the alert
      const statusText = uploadRes.statusText ? ` ${uploadRes.statusText}` : '';
      const finalErrorMessage = errorText ? errorText : `HTTP error ${uploadRes.status}${statusText}`;
      throw new Error(`Failed to upload file to S3: ${finalErrorMessage}`);
    }

    const bucket = process.env.REACT_APP_S3_BUCKET;
    const region = process.env.REACT_APP_AWS_REGION;
    if (!bucket || !region) {
        console.error("S3 bucket or region not configured in .env file.");
        throw new Error("S3 configuration missing.");
    }
    // The key already includes 'products/{productId}.png'
    return `https://${bucket}.s3.${region}.amazonaws.com/${returnedKey}`;
  }

// Save (add or edit) product
  const handleSaveProduct = async () => {
    if (isSavingProduct) return; // guard against double-clicks

    const errs = {};
    if (!formData.name.trim()) errs.name = "Product name is required.";
    if (!formData.productType) errs.productType = "Category is required.";
    if (!formData.code) errs.code = "Product Code is required.";
    if (!formData.unitOfMeasure) errs.unitOfMeasure = "Unit of Measure is required.";
    const unitsPerCaseProvided = formData.unitsPerCase !== "" && formData.unitsPerCase != null;
    const unitsPerCaseNumber = Number(formData.unitsPerCase);
    if (isUnitProduct(formData.unitOfMeasure) && !unitsPerCaseProvided) {
      errs.unitsPerCase = "Units per case is required for unit products.";
    } else if (unitsPerCaseProvided && (!Number.isInteger(unitsPerCaseNumber) || unitsPerCaseNumber <= 0)) {
      errs.unitsPerCase = "Units per case must be a positive whole number.";
    }
    if (formData.brand && !formData.brand.isNew && !formData.brand.id) errs.brand = "Brand selection is required.";
    if (formData.brand && formData.brand.isNew && !newBrand.name.trim()) errs.brand = "New Brand name is required.";
    if (!formData.brand) errs.brand = "Brand is required.";

    setErrors(errs);
    if (Object.keys(errs).length) return;

    setIsSavingProduct(true);

    const currentSelectedFile = selectedImageFile;
    let tempIdForUI = null; // Used for optimistic UI update for new products
    // Base payload from formData, excluding fields not for direct submission or handled differently
    let basePayload = { ...formData };
    delete basePayload.id;
    delete basePayload.promotion;
    delete basePayload.salesData;
    delete basePayload.topCustomers;

    let finalBackendPayload = { ...basePayload };

    if (finalBackendPayload.defaultPrices) {
      const prices = finalBackendPayload.defaultPrices;
      const parsedPrices = {
        tier1: prices.tier1 !== "" && !isNaN(parseFloat(prices.tier1)) ? parseFloat(prices.tier1) : null,
        tier2: prices.tier2 !== "" && !isNaN(parseFloat(prices.tier2)) ? parseFloat(prices.tier2) : null,
        tier3: prices.tier3 !== "" && !isNaN(parseFloat(prices.tier3)) ? parseFloat(prices.tier3) : null,
      };
      finalBackendPayload.defaultPrices = parsedPrices;
    }
    finalBackendPayload.unitsPerCase =
      isUnitProduct(finalBackendPayload.unitOfMeasure) &&
      finalBackendPayload.unitsPerCase !== "" &&
      finalBackendPayload.unitsPerCase != null
        ? Number(finalBackendPayload.unitsPerCase)
        : null;

    try {
      let updatedOrNewProduct;
      if (isEditing) {
        if (currentSelectedFile) {
          try {
            const newS3Url = await uploadToS3Internal(currentSelectedFile, formData.id);
            const finalS3Url = `${newS3Url}?timestamp=${new Date().getTime()}`;
            updatedOrNewProduct = await updateProduct(formData.id, { ...finalBackendPayload, image: finalS3Url });
          } catch (error) {
            console.error("S3 upload failed:", error);
            alert(`Image upload failed: ${error.message}`);
            updatedOrNewProduct = await updateProduct(formData.id, { ...finalBackendPayload, image: formData.image });
          }
        } else {
          updatedOrNewProduct = await updateProduct(formData.id, finalBackendPayload);
        }
      } else {
        let payloadForCreate = { ...finalBackendPayload };
        payloadForCreate.image = "";

        tempIdForUI = Date.now();
        const optimisticNewProductForUI = { ...formData, id: tempIdForUI, image: formData.image || placeholder };
        setData(d => ({ ...d, products: [...d.products, optimisticNewProductForUI] }));

        try {
          const savedProductFromBackend = await createProduct(payloadForCreate);
          updatedOrNewProduct = savedProductFromBackend;

          if (currentSelectedFile) {
            try {
              const s3Url = await uploadToS3Internal(currentSelectedFile, savedProductFromBackend.id);
              const finalS3Url = `${s3Url}?timestamp=${new Date().getTime()}`;
              const productWithImage = await updateProduct(savedProductFromBackend.id, { image: finalS3Url });
              updatedOrNewProduct = { ...savedProductFromBackend, ...productWithImage };
            } catch (uploadError) {
              console.error("S3 upload failed:", uploadError);
              alert(`Product created, but image upload failed: ${uploadError.message}`);
            }
          }
        } catch (err) {
          console.error("Failed to create product:", err);
          alert(`Failed to create product: ${err.message}`);
          setData(d => ({ ...d, products: d.products.filter(p => p.id !== tempIdForUI) }));
          return;
        }
      }

      // Ensure brand object is canonical in state right away
      const canonicalBrand = getCanonicalBrand(formData.brand);
      const productForState = {
        ...updatedOrNewProduct,
        brand: canonicalBrand || updatedOrNewProduct?.brand || null
      };
      const idToFind = isEditing ? productForState.id : tempIdForUI;

      setData(d => ({
        ...d,
        products: d.products.map(p => (p.id === idToFind ? productForState : p))
      }));

      setSelectedImageFile(null);
      closeDetailModal();
      closeAddModal();
    } catch (err) {
      console.error("Failed to save product:", err);
      alert(`Failed to save product: ${err.message}`);
    } finally {
      setIsSavingProduct(false);
    }
  };





  // Archive (soft delete) product
  const handleArchiveProduct = (id) => {
    // This function is currently not used by the new archive flow from the modal.
    // It might need to be updated or removed if it's intended for other purposes.
    // For now, the new handleConfirmArchive handles archiving from the detail modal.
    setData(d => ({
      ...d,
      products: d.products.map(p => (p.id === id ? { ...p, archived: true } : p))
    }));
    closeDetailModal();
  };

  // Prepare chart data for the sales line graph
const getChartData = (salesData = { labels: [], data: [] }) => ({
  labels: salesData.labels || [],
  datasets: [
    {
      label: "Monthly Sales",
      data: salesData.data || [],
      fill: false,
      backgroundColor: "#737894",
      borderColor: "#737894",
    },
  ],
});


  const handleImageError = (e) => {
    e.target.src = placeholder;
  };


  return (
    <div className="content" style={{ maxWidth: "1600px"}}>
      {/* Header with Add Product button and filters */}



<Row>

  <Col md="2">
  <FormGroup className="mb-0 d-flex align-items-center">
  <Label for="typeFilter" className="mr-2 mb-0">
        Category
      </Label>
      <Input
        type="select"
        id="typeFilter"
        value={selectedType}
        onChange={(e) => setSelectedType(e.target.value)}
      >
        <option value="">All</option>
        {categories.map((category) => (
          <option key={category.id} value={category.name}>
            {category.name}
          </option>
        ))}
      </Input>
    </FormGroup>
  </Col>

  {/* Brand Filter */}
  <Col md="2">
  <FormGroup className="mb-0 d-flex align-items-center">
  <Label for="typeFilter" className="mr-2 mb-0">
        Brand
      </Label>
      <Input
        type="select"
        id="brandFilter"
        value={selectedBrand}
        onChange={(e) => setSelectedBrand(e.target.value)}
      >
        <option value="">All</option>
        {brands.map((brand) => (
          <option key={brand.id} value={brand.name}>
            {brand.name}
          </option>
        ))}
      </Input>
    </FormGroup>
  </Col>

  {/* Sort Filter */}
  <Col md="2">
  <FormGroup className="mb-0 d-flex align-items-center">
  <Label for="sortByFilter" className="mr-2 mb-0"> {/* Changed for attribute for clarity */}
        Sort
      </Label>
      <Input
        type="select"
        id="sortByFilter" // Changed id for clarity
        value={sortBy}
        onChange={(e) => {
          const value = e.target.value;
          if (value === 'name') {
            setSortBy('name');
            setSortOrder('asc'); 
          } else if (value === 'price_asc') {
            setSortBy('price_asc');
            setSortOrder('asc');
          } else if (value === 'price_desc') {
            setSortBy('price_desc');
            setSortOrder('desc');
          } else {
            setSortBy(value); 
          }
        }}
      >
        <option value="name">Name A-Z</option>
        <option value="price_asc">Low To High (Price)</option>
        <option value="price_desc">High To Low (Price)</option>
      </Input>
    </FormGroup>
  </Col>

{isSuperuser && (
  <Col md="2" className="ml-auto d-flex justify-content-end">
    <div
      onClick={openAddModal}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: "pointer",
        transition: "background-color 0.3s",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0.5rem",
        borderRadius: "0.25rem",
        backgroundColor: hover ? "#f8f9fa" : "transparent"
      }}
    >
      <i className="nc-icon nc-cart-simple" style={{ fontSize: "1.5rem" }} />
      <p style={{ margin: 0, marginTop: "0.25rem", textAlign: "center" }}>
        Add New Product
      </p>
    </div>
  </Col>
)}
</Row>
<Row className="align-items-end mb-3">
  {/* Search */}
  <Col md="3">
    <FormGroup className="mb-0">
      <Label for="productSearch" className="mb-0">
        Search
      </Label>
      <Input
        id="productSearch"
        type="text"
        placeholder="Search by product name..."
        value={searchText}
        onChange={e => {
          setSearchText(e.target.value);
          setShowSearchResults(true);
        }}
      />
    </FormGroup>
  </Col>

  {/* Promo + Archived toggles, same height as input */}
  <Col md="4">
    <FormGroup check inline className="mb-0 d-flex align-items-center">
      <Label
        check
        className={`form-check-label px-3 py-1 rounded ${
          showPromotion ? "bg-primary text-white" : "bg-light text-dark"
        }`}
      >
        <Input
          type="checkbox"
          checked={showPromotion}
          onChange={e => setShowPromotion(e.target.checked)}
        />{" "}
        {showPromotion ? "Show All" : "On Promo"}
      </Label>
      <Label
        check
        className={`form-check-label px-3 py-1 rounded ms-2 ml-2 ${
          showArchived ? "bg-secondary text-white" : "bg-light text-dark"
        }`}
      >
        <Input
          type="checkbox"
          checked={showArchived}
          onChange={e => setShowArchived(e.target.checked)}
        />{" "}
        {showArchived ? "Remove Archived" : "Show Archived"}
      </Label>
    </FormGroup>
  </Col>
</Row>



       {/* Product Grid */}
       <Row>
          {filteredProducts.map((prod) => (
            <Col key={prod.id} xs="12" sm="4" md="4" lg="4" xl="2" className="mb-3 d-flex">
              <Card
                onClick={() => openDetailModal(prod)}
                className="h-100 flex-fill d-flex flex-column"
                style={{ cursor: "pointer" }}
              >
                <div
                  style={{
                    height: 300,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fff'
                  }}
                >
                  <img
                    src={
                      prod.image && prod.image.startsWith('http')
                        ? prod.image
                        : (prod.id ? s3ImageUrl("products", prod.id, ".png") : placeholder)
                    }
                    alt={prod.name}
                    loading="lazy"
                    style={{
                      maxHeight: '100%',
                      width: '100%',
                      objectFit: 'contain'
                    }}
                    onError={handleImageError}
                  />
                </div>
                <CardBody className="d-flex flex-column">
                  <h5 className="card-title">{prod.name}</h5>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
                    {prod.brand && prod.brand.name && (
                      <span className="badge bg-primary">
                        {prod.brand.name}
                      </span>
                    )}
                    {prod.productType && (
                      <span className="badge bg-secondary">
                        {prod.productType}
                      </span>
                    )}
                    {isUnitProduct(prod.unitOfMeasure) && prod.unitsPerCase != null && prod.unitsPerCase !== "" && (
                      <span className="badge bg-light text-dark">
                        {Number(prod.unitsPerCase).toLocaleString()}/case
                      </span>
                    )}
                  </div>
                  <p className="card-text flex-grow-1">
                    {/* reserved space for descriptions if added later */}
                  </p>
                  <CardFooter>
                    <div>
                      {prod.archived && (
                        <span className="badge bg-secondary"
                          style={{ position: 'absolute', left: '0.5rem', bottom: '0.5rem' }}
                        >
                          Archived
                        </span>
                      )}
                      {promotedProductIds.has(prod.id) && (
                        <span className="badge bg-info ml-2"
                          style={{ position: 'absolute', right: '0.5rem', bottom: '0.5rem' }}
                        >
                          Promo
                        </span>
                      )}
                    </div>
                  </CardFooter>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>


      {/* Product Detail Modal */}
      <Modal isOpen={detailModalOpen} toggle={closeDetailModal} size="lg">
        <ModalHeader toggle={closeDetailModal}>
          Product Details 
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="4">
            
              <img
                src={imageSrcForDetailModal}
                alt={formData.name || "Product image"}
                className="img-fluid"
                style={{ cursor: (isEditing && !formData.archived) ? "default" : "zoom-in" }}
                onClick={() => {
                    if (!(isEditing && !formData.archived)) { 
                      setModalImageUrl(imageSrcForDetailModal);
                      setImageModalOpen(true);
                    }
                  }}
                onError={(e) => {
                    if (imageSrcForDetailModal !== placeholder) { 
                      setImageSrcForDetailModal(placeholder);
                    }
                  }}
              />
              {isEditing && (
                <FormGroup>
                  <Label for="productImage">Update Image</Label>
                  <Input 
                    type="file" 
                    id="productImage" 
                    accept="image/*" 
                    onChange={handleImageUpload} 
                    disabled={formData.archived}
                  />
                </FormGroup>
              )}
            </Col>
            <Col md="8">
              <FormGroup>
                <Label for="productName">Product Name</Label>
                <Input
                  type="text"
                  id="productName"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  disabled={!isEditing}
                />
                                {errors.name && (
                  <small className="text-danger">{errors.name}</small>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="productCode">Product Code</Label>
                <Input
                  type="text"
                  id="productCode"
                  name="code"
                  value={formData.code}
                  onChange={handleFormChange}
                  disabled={!isEditing}
                />
                {errors.code && (
                  <small className="text-danger">{errors.code}</small>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="unitOfMeasure">Unit Of Measure</Label>
                <Input
                  type="select"
                  id="unitOfMeasure"
                  name="unitOfMeasure"
                  value={formData.unitOfMeasure}
                  onChange={handleFormChange}
                  disabled={!isEditing}
                >
                  <option value="">Select</option>
                  {unitOfMeasure.filter(uom => !uom.is_archived).map((unit) => (
                    <option key={unit.id} value={unit.name}>
                      {unit.name}
                    </option>
                  ))}
                </Input>
                                {errors.unitOfMeasure && (
                  <small className="text-danger">{errors.unitOfMeasure}</small>
                )}
              </FormGroup>
              {isUnitProduct(formData.unitOfMeasure) && (
                <FormGroup>
                  <Label for="unitsPerCase">Units Per Case</Label>
                  <Input
                    type="number"
                    id="unitsPerCase"
                    name="unitsPerCase"
                    min="1"
                    step="1"
                    value={formData.unitsPerCase ?? ""}
                    onChange={handleFormChange}
                    disabled={!isEditing}
                  />
                  {errors.unitsPerCase && (
                    <small className="text-danger">{errors.unitsPerCase}</small>
                  )}
                </FormGroup>
              )}
              <FormGroup>
                <Label for="productType">Category</Label>
                <Input
                  type="select"
                  id="productType"
                  name="productType"
                  value={formData.productType}
                  onChange={handleFormChange}
                  disabled={!isEditing}
                >
                  <option value="">Select</option>
                  {categories.filter(cat => !cat.is_archived).map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </Input>
                                {errors.productType && (
                  <small className="text-danger">{errors.productType}</small>
                )}
              </FormGroup>
              {/* Brand Selection / Add New Brand */}
              <FormGroup>
  <Label for="brandSelect">Brand</Label>
  <Input
    type="select"
    id="brandSelect"
    value={formData.brand.isNew ? "new" : formData.brand.name}
    onChange={(e) => {
      const selected = e.target.value;
      if (selected === "new") {
        // Set flag and clear new brand state:
        setFormData((prev) => ({
          ...prev,
          brand: { isNew: true, name: "" },
        }));
        setNewBrand({
          name: "",
          category: "",
          owner: "",
          description: "",
        });
      } else {
        const brandObj = brands.find((b) => b.name === selected);
        setFormData((prev) => ({ ...prev, brand: { ...brandObj, isNew: false } }));
      }
    }}
    disabled={!isEditing}
  >
   <option value="">Select</option>
    {brands.filter(b => !b.is_archived).map((b) => (
      <option key={b.id} value={b.name}>
        {b.name}
      </option>
    ))}

  </Input>
                  {errors.brand && (
                  <small className="text-danger">{errors.brand}</small>
                )}
</FormGroup>

{/* And then conditionally render new brand fields using the newBrand state: */}
 {isEditing && formData.brand.isNew && (
  <>
    <FormGroup>
      <Label for="newBrandNameField">New Brand Name</Label>
      <Input
        type="text"
        id="newBrandNameField"
        value={newBrand.name}
        onChange={(e) =>
          setNewBrand((prev) => ({ ...prev, name: e.target.value }))
        }
      />
    </FormGroup>
    <FormGroup>
      <Label for="newBrandCategoryField">Category</Label>
      <Input
        type="text"
        id="newBrandCategoryField"
        value={newBrand.category}
        onChange={(e) =>
          setNewBrand((prev) => ({ ...prev, category: e.target.value }))
        }
      />
    </FormGroup>
    <FormGroup>
      <Label for="newBrandOwnerField">Brand Owner</Label>
      <Input
        type="text"
        id="newBrandOwnerField"
        value={newBrand.owner}
        onChange={(e) =>
          setNewBrand((prev) => ({ ...prev, owner: e.target.value }))
        }
      />
    </FormGroup>
    <FormGroup>
      <Label for="newBrandDescriptionField">Description</Label>
      <Input
        type="textarea"
        id="newBrandDescriptionField"
        value={newBrand.description}
        onChange={(e) =>
          setNewBrand((prev) => ({ ...prev, description: e.target.value }))
        }
      />
    </FormGroup>
  </>
)} 


              {/* Default Prices */}
              <FormGroup>
                <Label>Price Type</Label>
                <Row>
                  <Col md="4">
                    <Input
                      type="text"
                      placeholder="Tier 1"
                      name="defaultPrices.tier1"
                      value={formData.defaultPrices.tier1}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                    />
                  </Col>
                  <Col md="4">
                    <Input
                      type="text"
                      placeholder="Tier 2"
                      name="defaultPrices.tier2"
                      value={formData.defaultPrices.tier2}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                    />
                  </Col>
                  <Col md="4">
                    <Input
                      type="text"
                      placeholder="Tier 3"
                      name="defaultPrices.tier3"
                      value={formData.defaultPrices.tier3}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                    />
                  </Col>
                </Row>
              </FormGroup>
              {/* <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    name="promotion"
                    checked={formData.promotion}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, promotion: e.target.checked }))
                    }
                    disabled={!isEditing}
                  />{" "}
                  Promotion
                </Label>
              </FormGroup> */}
            </Col>
          </Row>
          {/* Sales Chart and Top 5 Customers */}
          {/* <Row className="mt-4">
            <Col md="6">
              <Card>
                <CardHeader>
                  <CardTitle tag="h5">Sales by Month</CardTitle>
                </CardHeader>
                <CardBody>
                  <Line
                    data={getChartData(formData.salesData)}
                    options={{ maintainAspectRatio: false }}
                    height={200}
                  />
                </CardBody>
              </Card>
            </Col>
            <Col md="6">
              <Card>
                <CardHeader>
                  <CardTitle tag="h5">Top 5 Customers</CardTitle>
                </CardHeader>
                <CardBody>
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Volume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(formData.topCustomers || []).slice(0, 5).map((cust) => (
                        <tr key={cust.id}>
                          <td>{cust.name}</td>
                          <td>{cust.volume}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row> */}
        </ModalBody>
        <ModalFooter style={{ padding: '0.75rem 1rem' }}>
          {isEditing ? (
            <>
              <Button
                color={formData.archived ? "success" : "danger"}
                style={{ 
                  backgroundColor: formData.archived ? 'lightgreen' : 'lightcoral', 
                  marginRight: 'auto' 
                }}
                onClick={openArchiveConfirmModal}
                disabled={isSavingProduct}
              >
                {formData.archived ? "Unarchive" : "Archive"}
              </Button>
              <Button color="primary" onClick={handleSaveProduct} disabled={isSavingProduct}>
                {isSavingProduct ? (<><i className="fa fa-spinner fa-spin" /> Saving…</>) : "Save Changes"}
              </Button>
            </>
          ) : (
            <>
            {isSuperuser && (
            <Button color="primary" onClick={() => setIsEditing(true)} disabled={isSavingProduct}>
              Edit Product
            </Button>
            )}
            </>
          )}
          <Button color="secondary" onClick={closeDetailModal} disabled={isSavingProduct}>
            Close
          </Button> 
        </ModalFooter>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal isOpen={archiveConfirmModalOpen} toggle={closeArchiveConfirmModal} centered>
        <ModalHeader toggle={closeArchiveConfirmModal}>
          Confirm {formData.archived ? "Unarchive" : "Archive"}
        </ModalHeader>
        <ModalBody>
          Are you sure you want to {formData.archived ? "unarchive" : "archive"} this product?
        </ModalBody>
        <ModalFooter style={{ padding: '0.75rem 1rem' }}>
          <Button color={formData.archived ? "success" : "danger"} onClick={handleConfirmArchive}>
            Yes, {formData.archived ? "Unarchive" : "Archive"}
          </Button>
          <Button color="secondary" onClick={closeArchiveConfirmModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Image Zoom Modal */}
      <Modal isOpen={imageModalOpen} toggle={() => setImageModalOpen(false)} size="xl" centered>
        <ModalBody style={{ padding: 0 }}>
          <img
            src={modalImageUrl}
            alt="Full Screen"
            style={{ width: "100%", height: "auto", maxHeight: "90vh", objectFit: "contain" }}
            onError={handleImageError}
          />
        </ModalBody>
      </Modal>

      {/* Add Product Modal */}
      <Modal isOpen={addModalOpen} toggle={closeAddModal} size="lg">
        <ModalHeader toggle={closeAddModal}>Add New Product</ModalHeader>
        <ModalBody>
          <Row>
            <Col md="4">
              <img 
                src={formData.image || placeholder} 
                alt="Product" 
                className="img-fluid"
                onError={handleImageError}
              />
              <FormGroup>
                <Label for="newProductImage">Upload Image</Label>
                <Input type="file" id="newProductImage" accept="image/*" onChange={handleImageUpload} />
              </FormGroup>
            </Col>
            <Col md="8">
              <FormGroup>
                <Label for="newProductName">Product Name</Label>
                <Input
                  type="text"
                  id="newProductName"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                />
                 {errors.name && <small className="text-danger">{errors.name}</small>}
              </FormGroup>
              <FormGroup>
                <Label for="newProductCode">Product Code</Label>
                <Input
                  type="text"
                  id="newProductCode"
                  name="code"
                  value={formData.code}
                  onChange={handleFormChange}
                />
              {errors.code && <small className="text-danger">{errors.code}</small>}

              </FormGroup>
              <FormGroup>
                <Label for="newProductType">Category</Label>
                <Input
                  type="select"
                  id="newProductType"
                  name="productType"
                  value={formData.productType}
                  onChange={handleFormChange}
                >
                  <option value="">Select</option>
                  {categories.filter(cat => !cat.is_archived).map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </Input>
                {errors.productType && <small className="text-danger">{errors.productType}</small>}
              </FormGroup>
                            <FormGroup>
                <Label for="newProductUnit">Unit of Measure</Label>
                <Input
                  type="select"
                  id="newProductUnit"
                  name="unitOfMeasure"
                  value={formData.unitOfMeasure}
                  onChange={handleFormChange}
                >
                  <option value="">Select</option>
                  {unitOfMeasure.filter(uom => !uom.is_archived).map((unit) => (
                    <option key={unit.id} value={unit.name}>
                      {unit.name}
                    </option>
                  ))}
                </Input>
                 {errors.unitOfMeasure && <small className="text-danger">{errors.unitOfMeasure}</small>}
              </FormGroup>
              {isUnitProduct(formData.unitOfMeasure) && (
                <FormGroup>
                  <Label for="newProductUnitsPerCase">Units Per Case</Label>
                  <Input
                    type="number"
                    id="newProductUnitsPerCase"
                    name="unitsPerCase"
                    min="1"
                    step="1"
                    value={formData.unitsPerCase ?? ""}
                    onChange={handleFormChange}
                  />
                  {errors.unitsPerCase && <small className="text-danger">{errors.unitsPerCase}</small>}
                </FormGroup>
              )}
              <FormGroup>
                <Label for="newBrandSelect">Brand</Label>
                  <Input
                    type="select"
                    id="newBrandSelect"
                    value={isAddingBrand ? "new" : (formData.brand.name || "")}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      if (selectedValue === "new") {
                        setIsAddingBrand(true);
                        // Ensure formData reflects new brand, and reset newBrand state for inputs
                        setFormData((prev) => ({
                          ...prev,
                          brand: { ...prev.brand, isNew: true, name: "", id: "" }, 
                        }));
                        setNewBrand({ name: "", category: "", owner: "", description: "" });
                      } else {
                        // Existing brand selected
                        setIsAddingBrand(false);
                        const existingBrandData = brands.find((b) => b.name === selectedValue);
                        setFormData((prev) => ({
                          ...prev,
                          brand: { ...(existingBrandData || { name: selectedValue, id: selectedValue }), isNew: false },
                        }));
                      }
                    }}
                  >
                    <option value="">Select</option>
                    {brands.filter(b => !b.is_archived).map((b) => (
                      <option key={b.id} value={b.name}>
                        {b.name}
                      </option>
                    ))}
                  </Input>
                  {errors.brand && <small className="text-danger">{errors.brand}</small>}
              </FormGroup>
              {/* {isAddingBrand && ( // Control visibility using isAddingBrand
                <>
                  <FormGroup>
                    <Label for="newBrandNameField">New Brand Name</Label>
                    <Input
                      type="text"
                      id="newBrandNameField"
                      value={newBrand.name} // Bind to newBrand state
                      onChange={(e) => setNewBrand(prev => ({...prev, name: e.target.value}))} // Update newBrand state
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="newBrandCategoryField">Category</Label>
                    <Input
                      type="text"
                      id="newBrandCategoryField"
                      value={newBrand.category} // Bind to newBrand state
                      onChange={(e) => setNewBrand(prev => ({...prev, category: e.target.value}))} // Update newBrand state
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="newBrandOwnerField">Brand Owner</Label>
                    <Input
                      type="text"
                      id="newBrandOwnerField"
                      value={newBrand.owner} // Bind to newBrand state
                      onChange={(e) => setNewBrand(prev => ({...prev, owner: e.target.value}))} // Update newBrand state
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="newBrandDescriptionField">Description</Label>
                    <Input
                      type="textarea"
                      id="newBrandDescriptionField"
                      value={newBrand.description} // Bind to newBrand state
                      onChange={(e) => setNewBrand(prev => ({...prev, description: e.target.value}))} // Update newBrand state
                    />
                  </FormGroup>
                </>
              )} */}
              <FormGroup>
                <Label>Default Prices</Label>
                <Row>
                  <Col md="4">
                    <Input
                      type="text"
                      placeholder="Tier 1"
                      name="defaultPrices.tier1"
                      value={formData.defaultPrices.tier1}
                      onChange={handleFormChange}
                    />
                  </Col>
                  <Col md="4">
                    <Input
                      type="text"
                      placeholder="Tier 2"
                      name="defaultPrices.tier2"
                      value={formData.defaultPrices.tier2}
                      onChange={handleFormChange}
                    />
                  </Col>
                  <Col md="4">
                    <Input
                      type="text"
                      placeholder="Tier 3"
                      name="defaultPrices.tier3"
                      value={formData.defaultPrices.tier3}
                      onChange={handleFormChange}
                    />
                  </Col>
                </Row>
              </FormGroup>
            </Col>
          </Row>
      
        </ModalBody>
        <ModalFooter style={{ padding: '0.75rem 1rem' }}>
          <Button color="primary" onClick={handleSaveProduct} disabled={isSavingProduct}>
            {isSavingProduct ? (<><i className="fa fa-spinner fa-spin" /> Saving…</>) : (isEditing ? "Save Changes" : "Add Product")}
          </Button>
          <Button color="secondary" onClick={closeAddModal} disabled={isSavingProduct}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
    
  );
};

export default Products;
